import React from 'react';
import styled from 'styled-components';
import {Content} from 'components/Content';

import {Link} from 'gatsby-plugin-react-i18next';

import BlogSidebar, {
  blogSidebarContainerWidth,
  blogSidebarContainerWidthLg
} from 'components/blog/BlogSidebar';
import BlogBanner from 'components/blog/BlogBanner';
import {BlogCategoryType, BlogPost} from '../../types/BlogTypes';
import BackToTop from 'components/BackToTop';
import {StyledDiv} from 'components/StyledSx';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton
} from 'react-share';
import {RichText} from 'prismic-reactjs';
import {links} from '../global.config';
import {Flex} from 'components/Flex';
import {containerSize, halfContainerSize, ScreenSizes} from '../../types/responsive';

type Props = {
  formBanner?: boolean;
  categories: BlogCategoryType[];
  activeCategory?: string;
  popularPosts: BlogPost[];
  mustReadPosts: BlogPost[];
  post?: BlogPost;
  uri?: string;
};

const Container = styled.div`
  scroll-behavior: smooth;
`;

const BannerBG = styled.div`
  background-color: #7984d9;
  mix-blend-mode: multiply;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const BannerContainer = styled.div`
  position: relative;
`;

const BannerContent = styled(Content)`
  display: flex;
  gap: 5rem;
  align-items: center;
  padding: 45px 20px;
`;

const BannerLink = styled(Link as any)`
  font-style: normal;
  font-weight: 300;
  font-size: 39px;
  line-height: 48px;
  color: #ffffff;
  padding: unset;
  margin: unset;
  transition: color 0.2s ease;

  &:hover {
    color: #f4f6fc;
  }
`;

const CategoriesContainer = styled.div`
  border-bottom: 1px solid;
  border-top: 1px solid;
  border-image-slice: 1;
  border-image-source: linear-gradient(45deg, #00e9d5, #7984d9);
  overflow-x: auto;
`;

const CategoriesContent = styled(Content)`
  padding: 1.75rem 20px;

  @media (max-width: ${ScreenSizes.md}) {
    width: fit-content;
  }
`;

const CategoriesList = styled.ul`
  margin: unset;
  display: flex;
  gap: 1.75rem;
  list-style: none;

  @media (min-width: 1440px) {
    gap: 3.625rem;
  }
`;

const CategoryItem = styled.li`
  margin: unset;
  width: max-content;
  flex-shrink: 0;
`;

const CategoryLink = styled(Link)`
  padding: unset;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.05em;
  color: #475157;

  &.active {
    color: #8554a6;
  }
`;

const BlogContent = styled(StyledDiv)`
  margin: 60px auto 0;
  position: relative;
  display: flex;
  justify-content: flex-start;
  @media (max-width: ${ScreenSizes.lg}) {
    flex-direction: column-reverse;
  }
  @media (max-width: 768px) {
    margin: 25px auto 0;
  }
`;

const ShareButtons = styled.div`
  position: relative;
  width: calc(50vw - ${halfContainerSize} + 10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  button {
    margin: 3px 0;
    opacity: 0.2;
    transition: opacity 0.2s ease-in-out;
    position: fixed;
    &:first-child {
      transform: translateY(-45px);
    }
    &:last-child {
      transform: translateY(45px);
    }
    &:hover {
      opacity: 1;
    }
  }

  @media (max-width: ${ScreenSizes.lg}) {
    flex-direction: row;
    justify-content: center;
    width: 100%;
    grid-gap: 15px;
    margin: 40px auto -20px;
    button {
      position: relative;
      &:first-child,
      &:last-child {
        transform: none;
      }
    }
  }
`;

const iconProps = {
  bgStyle: {fill: 'currentColor'},
  size: 32,
  round: true
};

const ContentWrapper = styled.div`
  width: calc(${containerSize} - ${blogSidebarContainerWidth} - 90px);
  margin: 0px 50px 0px 10px;

  @media (max-width: ${ScreenSizes.lg}) {
    width: calc(100% - ${blogSidebarContainerWidthLg});
    max-width: calc(${containerSize} - ${blogSidebarContainerWidthLg} - 60px);
    margin: auto 20px;
  }
  @media (max-width: ${ScreenSizes.md}) {
    width: 100%;
    margin: auto 20px;
  }

  article {
    blockquote {
      color: #9d74b9;
      font-size: 1.1rem;
      background: rgb(241, 242, 242);
      border-radius: 8px;
      padding: 14px 24px;
      margin: 0;
      font-style: italic;
    }
  }
`;

const BlogLayout: React.FC<Props> = ({
  formBanner,
  categories,
  activeCategory,
  children,
  post,
  uri,
  ...props
}) => {
  const postUrl = post ? `${links.landing}${uri}` : '';
  return (
    <Container {...props}>
      {formBanner ? (
        <BlogBanner />
      ) : (
        <BannerContainer>
          <BannerBG />
          <BannerContent>
            <BannerLink to="/blog/">MONEI Blog</BannerLink>
          </BannerContent>
        </BannerContainer>
      )}
      <CategoriesContainer>
        <CategoriesContent>
          <CategoriesList>
            {categories
              .sort((a, b) => (a.order < b.order ? -1 : 1))
              .map((item) => {
                return (
                  <CategoryItem key={item.slug}>
                    <CategoryLink
                      to={`/blog/${item.slug}`}
                      activeClassName="active"
                      className={activeCategory === item.slug ? 'active' : ''}>
                      {item.title}
                    </CategoryLink>
                  </CategoryItem>
                );
              })}
          </CategoriesList>
        </CategoriesContent>
      </CategoriesContainer>
      <BlogContent>
        {post ? (
          <ShareButtons>
            <FacebookShareButton
              url={postUrl}
              quote={RichText.asText(post.title)}
              hashtag="#digitalpayments">
              <FacebookIcon {...iconProps} />
            </FacebookShareButton>
            <LinkedinShareButton
              url={postUrl}
              title={RichText.asText(post.title)}
              summary={!!post.summary ? RichText.asText(post.summary) : ''}
              source="MONEI">
              <LinkedinIcon {...iconProps} />
            </LinkedinShareButton>
            <TwitterShareButton
              url={postUrl}
              title={RichText.asText(post.title)}
              via="MONEI"
              hashtags={['ecommerce', 'digitalpayments']}>
              <TwitterIcon {...iconProps} />
            </TwitterShareButton>
          </ShareButtons>
        ) : (
          <ShareButtons />
        )}
        <Flex sx={{justifyContent: {all: 'flex-start', lg: 'center'}}}>
          <ContentWrapper id="blog-content-wrapper">{children}</ContentWrapper>
          <BlogSidebar popularPosts={props.popularPosts} mustReadPosts={props.mustReadPosts} />
        </Flex>
      </BlogContent>
      <BackToTop />
    </Container>
  );
};

export default BlogLayout;
